import { useEffect, useRef } from "react";
import {
  disableInlineScripts,
  activateDeferredScripts,
  logDeferredCmsHtmlServer
} from "../lib/utils/cms-deferred-scripts";

/**
 * Renders CMS HTML where inline <script> tags are rewritten to a
 * non-executable type on the server so the browser parser skips them.
 * SSR and CSR markup are byte-identical, so hydration sees no script
 * mismatch. After mount, the placeholders are replaced in place with
 * real <script> elements so they execute at their authored DOM position.
 */
export const DeferredCmsHtml = ({ html, logTrace = "", blockName = "" }) => {
  const containerRef = useRef(null);
  const lastRunRef = useRef(null);

  useEffect(() => {
    if (!html || typeof html !== "string" || !/<script\b/i.test(html)) {
      return;
    }
    if (lastRunRef.current === html) return;
    lastRunRef.current = html;
    const p = activateDeferredScripts(containerRef.current);
    if (p && typeof p.catch === "function") {
      p.catch((e) =>
        console.warn("[cms-deferred-scripts] activation error:", e)
      );
    }
  }, [html]);

  if (!html) return null;

  if (typeof window === "undefined") {
    logDeferredCmsHtmlServer(html, logTrace, blockName);
  }

  return (
    <div
      ref={containerRef}
      dangerouslySetInnerHTML={{ __html: disableInlineScripts(html) }}
    />
  );
};

/**
 * Wraps children (e.g. a codeblock component) and activates any deferred
 * scripts inside the rendered DOM after hydration. This lets the child
 * component render its own markup (preserving classes, structure) while
 * still deferring script execution.
 */
export const DeferredScriptWrapper = ({ html, logTrace = "", blockName = "", children }) => {
  const containerRef = useRef(null);
  const lastRunRef = useRef(null);

  useEffect(() => {
    if (!html || typeof html !== "string" || !/<script\b/i.test(html)) {
      return;
    }
    if (lastRunRef.current === html) return;
    lastRunRef.current = html;
    const p = activateDeferredScripts(containerRef.current);
    if (p && typeof p.catch === "function") {
      p.catch((e) =>
        console.warn("[cms-deferred-scripts] activation error:", e)
      );
    }
  }, [html]);

  if (typeof window === "undefined") {
    logDeferredCmsHtmlServer(html, logTrace, blockName);
  }

  return <div ref={containerRef}>{children}</div>;
};
